// Typed asset endpoint functions. Backed by GET /api/v1/assets/*.
//
// Per CHG-1234 plan §1.1 — consumed by @generated/screens/asset-list.ts +
// asset-detail.ts queryFn callbacks. Spec: specs/tier-04-interface/asset-library.api.yaml.

import { apiClient } from "./client";
import type {
  Asset,
  AssetPagedEnvelope,
  AssetUsage,
  AssetVersion,
  PagedEnvelope,
} from "./types";

export interface ListAssetsParams {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  search?: string;
  kind?: string;     // photo | logo | illustration | pattern | video
  source?: string;   // uploaded | ai_generated | platform_authentic
  tags?: string[];
}

export async function listAssets(params: ListAssetsParams = {}): Promise<{ items: Asset[]; total: number }> {
  const r = await apiClient().get<AssetPagedEnvelope>("/assets", {
    params: {
      offset: (params.page ?? 0) * (params.pageSize ?? 50),
      limit: params.pageSize ?? 50,
      sort: params.sortField ? `${params.sortField}:${params.sortDirection ?? "asc"}` : undefined,
      q: params.search,
      kind: params.kind,
      source: params.source,
      tags: params.tags?.join(","),
    },
  });
  return { items: r.data.items, total: r.data.total };
}

export async function getAsset(id: string): Promise<Asset> {
  const r = await apiClient().get<Asset>(`/assets/${id}`);
  return r.data;
}

export async function listAssetUsages(id: string): Promise<PagedEnvelope<AssetUsage>> {
  const r = await apiClient().get<PagedEnvelope<AssetUsage>>(`/assets/${id}/usages`);
  return r.data;
}

export async function listAssetVersions(id: string): Promise<PagedEnvelope<AssetVersion>> {
  const r = await apiClient().get<PagedEnvelope<AssetVersion>>(`/assets/${id}/versions`);
  return r.data;
}

export async function getAssetVersion(id: string, versionNumber: number): Promise<AssetVersion> {
  const r = await apiClient().get<AssetVersion>(`/assets/${id}/versions/${versionNumber}`);
  return r.data;
}

export const assetsApi = {
  listAssets,
  getAsset,
  listAssetUsages,
  listAssetVersions,
  getAssetVersion,
};
